import { Lightbulb } from "lucide-react";
import type { ParsedQuery } from "../../../shared/contracts.js";
import { Badge } from "../../components/ui/Badge.js";
import { Tooltip } from "../../components/ui/Tooltip.js";
import { fmtPhone, fmtPlate, fmtRut, titleCase } from "../../lib/format.js";
import { PLATE_STRENGTH_LABELS, warningText } from "../../lib/labels.js";
import { vinSummary } from "../../lib/vin.js";
import { modelTyped } from "./advanced.js";

interface Chip {
  key: string;
  label: string;
  value: string;
  hint?: string;
  weak?: boolean;
}

const VIA_HINT = {
  alias: "reconocida por nombre o alias",
  fuzzy: "corregida por parecido (posible error de tipeo)",
  inferred: "deducida a partir del modelo",
  forced: "indicada en los filtros",
} as const;

/** Interpretación de la caja única → chips legibles: el analista ve qué entendió el sistema. */
export function parsedChips(p: ParsedQuery, now?: Date): Chip[] {
  const chips: Chip[] = [];
  for (const pl of p.plates) {
    chips.push({
      key: `plate-${pl.plate}`,
      label: "Patente",
      value: fmtPlate(pl.plate),
      hint: PLATE_STRENGTH_LABELS[pl.strength],
      weak: pl.strength === "weak",
    });
  }
  if (p.platePrefix) {
    chips.push({ key: "prefix", label: "Patente que empieza con", value: p.platePrefix.toUpperCase() });
  }
  if (p.vin) {
    chips.push({ key: "vin", label: p.vin.partial ? "VIN parcial" : "VIN", value: p.vin.value.toUpperCase() });
    // Lo que el propio VIN dice (fabricante, año modelo), cruzado con la marca y el año del caso.
    const read = p.vin.partial ? null : vinSummary(p.vin.value, {
      ...(p.make ? { make: p.make.value } : {}), ...(p.year ? { yearFrom: p.year.from, yearTo: p.year.to } : {}), ...(now ? { now } : {}),
    });
    if (read) chips.push({ key: "vin-read", label: "Según el VIN", value: read.value, hint: read.notes.join("; "), weak: read.mismatch });
  }
  for (const r of p.ruts) {
    chips.push({
      key: `rut-${r.body}`,
      label: "RUT",
      value: fmtRut(r.body, r.dv),
      hint: r.valid ? "dígito verificador correcto" : "dígito verificador NO calza: revisa el RUT",
      weak: !r.valid,
    });
  }
  for (const ph of p.phones) chips.push({ key: `ph-${ph}`, label: "Teléfono", value: fmtPhone(ph) });
  if (p.make) chips.push({ key: "make", label: "Marca", value: titleCase(p.make.value), hint: VIA_HINT[p.make.via] });
  if (p.model) {
    chips.push({ key: "model", label: "Modelo", value: titleCase(modelTyped(p)),
      hint: p.model.free ? "indicado en los filtros; no está en el catálogo: se busca tal cual" : VIA_HINT[p.model.via] });
  }
  if (p.year) {
    chips.push({ key: "year", label: "Año", value: p.year.from === p.year.to ? String(p.year.from) : `${p.year.from}–${p.year.to}` });
  }
  if (p.color) chips.push({ key: "color", label: "Color", value: titleCase(p.color) });
  if (p.location) {
    const where = [p.location.comuna, p.location.region].filter(Boolean).map((v) => titleCase(v)).join(", ");
    if (where) {
      chips.push({
        key: "loc",
        label: "Lugar",
        value: where,
        hint: p.location.soft ? "solo ordena los resultados, no filtra" : undefined,
        weak: p.location.soft,
      });
    }
  }
  if (p.person) chips.push({ key: "person", label: "Persona", value: `«${p.person.text}»` });
  for (const [i, o] of (p.persons ?? []).entries()) chips.push({ key: `person-${i}`, label: "Persona", value: `«${o.text}»` });
  for (const m of p.soft.models) {
    chips.push({ key: `sm-${m}`, label: "¿Modelo?", value: titleCase(m), hint: "interpretación posible: solo suma puntaje", weak: true });
  }
  if (p.freeText.length > 0) chips.push({ key: "text", label: "Texto", value: p.freeText.join(" ") });
  for (const e of p.emails ?? []) chips.push({ key: `mail-${e}`, label: "Correo", value: e });
  if (p.company?.name) chips.push({ key: "company", label: "Empresa", value: p.company.name });
  else if (p.company?.rut) chips.push({ key: "company", label: "RUT de empresa", value: p.company.rut });
  if (p.boostText?.length) {
    chips.push({ key: "addr", label: "Dirección", value: p.boostText.join(" "),
      hint: "ordena los resultados; su calle se busca en noticias del robo o choque del vehículo (nunca junto al nombre)", weak: true });
  }
  if (p.incident) {
    chips.push({
      key: "inc", label: "Siniestro", value: p.incident.date,
      hint: `${p.incident.onlyWindow ? "solo" : "prioriza"} avisos entre ${p.incident.daysBefore} días antes y ${p.incident.daysAfter} después`,
    });
  }
  return chips;
}

export function ParsedSummary({ parsed }: { parsed: ParsedQuery }) {
  const chips = parsedChips(parsed);
  const warnings = parsed.warnings.filter((w) => w !== "empty_query");
  if (chips.length === 0 && warnings.length === 0) return null;
  return (
    <section aria-label="Interpretación de la búsqueda" className="space-y-2">
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[12.5px] font-medium text-muted">Detectamos:</span>
          {chips.map((c) => {
            const badge = (
              <Badge
                size="md"
                tone={c.weak ? "outline" : "accent"}
                className={c.weak ? "border-dashed" : undefined}
              >
                <span className="font-normal opacity-80">{c.label}</span>
                <span className="max-w-[18rem] truncate font-semibold">{c.value}</span>
              </Badge>
            );
            return c.hint ? (
              <Tooltip key={c.key} content={c.hint}>
                {badge}
              </Tooltip>
            ) : (
              <span key={c.key}>{badge}</span>
            );
          })}
        </div>
      )}
      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((w) => (
            <li key={w} className="flex items-start gap-1.5 text-[12.5px] text-warning-soft-fg">
              <Lightbulb className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden="true" />
              <span>{warningText(w)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
